import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Pause, Play } from "lucide-react";

const HeroSection = () => {
  const [isPlaying, setIsPlaying] = useState(true);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const videoRef = useRef(null);

  const videos = [
    "/videos/robot-flower-hero.mp4",
    "/videos/emotional-ai-hero.mp4",
    "/videos/tech-hero.mp4",
  ];

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current
        .play()
        .catch((err) => console.log("Auto-play prevented:", err));
    }
  }, [currentVideoIndex]);

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) videoRef.current.pause();
      else videoRef.current.play();
      setIsPlaying(!isPlaying);
    }
  };

  const handleVideoEnd = () => {
    setCurrentVideoIndex((prev) => (prev + 1) % videos.length);
  };

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const fade = (delay = 0) => ({
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] },
  });

  return (
    <section
      id="home"
      className="relative h-screen flex items-center overflow-hidden"
    >
      {/* Video background */}
      <div className="absolute inset-0 w-full h-full">
        <video
          ref={videoRef}
          className="w-full h-full object-cover"
          style={{
            minWidth: "100%",
            minHeight: "100%",
            width: "auto",
            height: "auto",
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
          muted
          playsInline
          onEnded={handleVideoEnd}
          key={currentVideoIndex}
        >
          <source src={videos[currentVideoIndex]} type="video/mp4" />
        </video>
      </div>

      {/* Gradient + grain overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-black/65 via-black/55 to-blue-950/55" />
      <div
        className="pointer-events-none absolute inset-0 opacity-20 mix-blend-overlay"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
          backgroundSize: "28px 28px",
        }}
      />

      {/* Pause/Play */}
      <button
        onClick={togglePlayPause}
        className="absolute bottom-6 sm:bottom-8 right-4 sm:right-8 w-10 h-10 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center hover:bg-white/20 transition-all z-20 border border-white/20"
        aria-label={isPlaying ? "Pause video" : "Play video"}
        data-testid="hero-video-toggle"
      >
        {isPlaying ? (
          <Pause className="w-4 h-4 text-white" />
        ) : (
          <Play className="w-4 h-4 text-white ml-0.5" />
        )}
      </button>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 sm:px-10">
        <div className="max-w-3xl">
          {/* Publication header — MIT journal style */}
          <motion.div
            {...fade(0.18)}
            className="flex items-center gap-4 mb-8 pb-3 border-b border-white/20"
          >
            <span className="text-[10px] font-mono tracking-[0.32em] uppercase text-white/90">
              Vol. 01 / Winter MMXXVI
            </span>
            <span className="text-white/30">—</span>
            <span className="text-[10px] font-mono tracking-[0.32em] uppercase text-[#00BCD4]">
              Research Agenda
            </span>
          </motion.div>

          {/* Title */}
          <motion.h1
            {...fade(0.26)}
            className="text-4xl sm:text-5xl lg:text-7xl font-light text-white tracking-tight leading-[1.02] mb-5"
          >
            Where humans and{" "}
            <span className="text-white/45">machines</span>
            <br />
            <span className="text-white/45">understand</span> each other.
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            {...fade(0.36)}
            className="text-sm sm:text-base text-white/75 font-light leading-relaxed max-w-xl mb-10"
          >
            We build AI that is biomedically grounded, cognitively informed, and
            humanly accountable — through interdisciplinary research that fuses
            neuroscience, data, and the humanities.
          </motion.p>

          {/* CTAs — flat MIT-style text links with arrows */}
          <motion.div
            {...fade(0.46)}
            className="flex flex-col sm:flex-row gap-6 sm:gap-10"
          >
            <button
              onClick={() => scrollTo("demo")}
              data-testid="hero-cta-demo"
              className="group inline-flex items-center gap-3 text-left text-white"
            >
              <span className="text-[10px] font-mono tracking-[0.3em] uppercase text-[#00BCD4]">
                01
              </span>
              <span className="flex items-center gap-2 text-base font-light tracking-tight border-b border-white/30 group-hover:border-white pb-1 transition-colors">
                Try the live demo
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </span>
            </button>
            <button
              onClick={() => scrollTo("team")}
              data-testid="hero-cta-domains"
              className="group inline-flex items-center gap-3 text-left text-white"
            >
              <span className="text-[10px] font-mono tracking-[0.3em] uppercase text-[#00BCD4]">
                02
              </span>
              <span className="flex items-center gap-2 text-base font-light tracking-tight border-b border-white/30 group-hover:border-white pb-1 transition-colors">
                Explore research domains
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </span>
            </button>
          </motion.div>

          {/* Footer micro-data */}
          <motion.div
            {...fade(0.6)}
            className="mt-14 flex flex-wrap items-baseline gap-x-8 gap-y-2 pt-6 border-t border-white/15 max-w-xl"
          >
            {[
              { v: "06", l: "Research Domains" },
              { v: "24", l: "Faculty & PIs" },
              { v: "48+", l: "Publications" },
            ].map((s, i, arr) => (
              <div key={s.l} className="flex items-baseline gap-2">
                <span className="text-2xl font-light text-white tracking-tight">
                  {s.v}
                </span>
                <span className="text-[10px] uppercase tracking-[0.25em] text-white/60">
                  {s.l}
                </span>
                {i < arr.length - 1 && (
                  <span className="text-white/20 ml-3 hidden sm:inline">/</span>
                )}
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="hidden sm:block absolute bottom-10 left-1/2 -translate-x-1/2">
        <div className="flex flex-col items-center gap-2">
          <span className="text-[9px] font-mono uppercase tracking-[0.3em] text-white/40">
            Scroll
          </span>
          <div className="w-px h-10 bg-gradient-to-b from-white/40 to-transparent" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
